import { useTranslation } from "react-i18next"
import { File, Play, SpeakerLow, X } from "@phosphor-icons/react"
import { ScrollArea } from "@/components/ui/scroll-area"
import { cn } from "@/lib/utils"

type FilePreviewsProps = {
  value: File[]
  isSubmitting: boolean
  isUploading: boolean
  errorFileName?: string
  onRemove: (file: File) => void
}

const FilePreviews = ({ value, isSubmitting, isUploading, errorFileName, onRemove }: FilePreviewsProps) => {
  const { t } = useTranslation()

  const getFileIcon = (file: { preview?: string; type: string; name: string }) => {
    if (file.preview) {
      return <img src={file.preview} className="h-full w-full rounded-md object-cover" alt={file.name} />
    } else if (file.type.startsWith("video/")) {
      return <Play className="text-on-surface-subdued mx-auto h-5 w-12" />
    } else if (file.type.startsWith("audio/")) {
      return <SpeakerLow className="text-on-surface-subdued mx-auto h-5" />
    } else {
      return <File className="text-on-surface-subdued mx-auto h-5" />
    }
  }

  return (
    <ScrollArea>
      <div className="flex max-h-80 flex-wrap justify-center gap-2 pt-4 sm:justify-start">
        {value.map((file, idx) => {
          const key = file.name + idx
          return (
            <div
              data-testid="file-preview-item"
              key={key}
              title={file.name}
              className={cn(
                "bg-surface-subdued relative flex h-16 w-16 items-center justify-center rounded-md border",
                errorFileName === file.name && "border-on-critical-surface",
              )}>
              {getFileIcon(file)}
              <button
                type="button"
                title={`${t("common.button.remove")} ${file.name}`}
                disabled={isSubmitting || isUploading}
                className={cn(
                  "border-surface bg-primary text-surface absolute -top-2 -right-2 h-6 w-6 overflow-hidden rounded-full border-2 transition-all enabled:hover:scale-125",
                  errorFileName === file.name && "bg-on-critical-surface",
                )}
                onClick={() => onRemove(file)}>
                <X className="h-full w-full p-0.5" />
              </button>
            </div>
          )
        })}
      </div>
    </ScrollArea>
  )
}

export { FilePreviews }
